import { useEffect, useRef, useState, useCallback } from 'react'
import type { GeoJSONFeature, MapMouseEvent } from 'mapbox-gl'
import type { LayerInfo, LayerEventHandlers, PopoverInfo } from '../types'

export function useMapLayers(mapRef: React.MutableRefObject<mapboxgl.Map | null>) {
  const [popoverInfo, setPopoverInfo] = useState<PopoverInfo | null>(null)
  const layerRegistry = useRef<Map<string, LayerInfo>>(new Map())
  const layerEventHandlers = useRef<Map<string, LayerEventHandlers>>(new Map())
  const boundLayers = useRef<
    Map<
      string,
      {
        onMouseEnter: (e: MapMouseEvent) => void
        onMouseLeave: (e: MapMouseEvent) => void
        onClick: (e: MapMouseEvent) => void
      }
    >
  >(new Map())
  const currentHoveredLayer = useRef<string | null>(null)

  function isLayerOnMap(layerId: string): boolean {
    try {
      return mapRef.current?.getLayer(layerId) !== undefined
    } catch {
      return false
    }
  }

  function retrieveTopLayerId(e: MapMouseEvent): string | null {
    const map = mapRef.current
    if (!map) return null

    const allFeatures = map.queryRenderedFeatures(e.point, {
      layers: Array.from(boundLayers.current.keys()),
    })

    const layerPriority = Array.from(boundLayers.current.keys()).reverse()
    for (const priorityLayerId of layerPriority) {
      if (allFeatures.some((feature) => feature?.layer?.id === priorityLayerId)) {
        return priorityLayerId
      }
    }
    return null
  }
  function bindLayerEvents(layerId: string): void {
    const map = mapRef.current
    if (!map || boundLayers.current.has(layerId)) return

    // Double-check layer exists
    if (!isLayerOnMap(layerId)) return

    const onMouseEnter = (e: MapMouseEvent): void => {
      const topLayerId = retrieveTopLayerId(e)

      if (topLayerId !== layerId) return

      if (e.features && e.features.length > 0) {
        const feature = e.features[0]
        currentHoveredLayer.current = layerId
        if (layerEventHandlers.current.has(layerId)) {
          layerEventHandlers.current.get(layerId)?.onMouseEnter?.(feature)
        }
      }
    }

    const onMouseLeave = (e: MapMouseEvent): void => {
      const map = mapRef.current
      if (!map) return

      const topLayerId = retrieveTopLayerId(e)

      if (currentHoveredLayer.current !== layerId || topLayerId !== layerId) {
        if (layerEventHandlers.current.has(layerId)) {
          layerEventHandlers.current.get(layerId)?.onMouseLeave?.(e.features?.[0] || null)
        }
      }
    }

    const onClick = (e: MapMouseEvent): void => {
      const map = mapRef.current
      if (!map) return
      e.originalEvent?.stopPropagation?.()
      const topLayerId = retrieveTopLayerId(e)

      if (topLayerId !== layerId) return

      if (e.features && e.features.length > 0) {
        const feature = e.features[0]
        const eventHandler = layerEventHandlers.current.get(layerId)
        if (eventHandler?.onClick) {
          eventHandler.onClick(feature)
        }
      }
    }

    try {
      map.on('mouseenter', layerId, onMouseEnter)
      map.on('mouseleave', layerId, onMouseLeave)
      map.on('click', layerId, onClick)
      boundLayers.current.set(layerId, { onMouseEnter, onMouseLeave, onClick })
    } catch (error) {
      console.warn(`Failed to bind events for layer ${layerId}:`, error)
    }
  }

  const unbindLayerEvents = (layerId: string): void => {
    const map = mapRef.current
    if (!map) return

    const layer = boundLayers.current.get(layerId)
    if (!layer) return

    const { onMouseEnter, onMouseLeave, onClick } = layer
    try {
      map.off('mouseenter', layerId, onMouseEnter)
      map.off('mouseleave', layerId, onMouseLeave)
      map.off('click', layerId, onClick)
      boundLayers.current.delete(layerId)
    } catch (error) {
      console.warn(`Failed to unbind events for layer ${layerId}:`, error)
    }
  }

  const registerLayer = useCallback(
    (layerId: string, eventHandlers: LayerEventHandlers): void => {
      layerRegistry.current.set(layerId, {
        id: layerId,
        ...eventHandlers,
        isVisible: true,
      })

      if (eventHandlers) {
        layerEventHandlers.current.set(layerId, eventHandlers)
      }

      // Try to bind events immediately if layer exists
      if (mapRef.current && isLayerOnMap(layerId)) {
        bindLayerEvents(layerId)
      }
    },
    [mapRef.current]
  )

  const unregisterLayer = useCallback(
    (layerId: string): void => {
      layerRegistry.current.delete(layerId)
      layerEventHandlers.current.delete(layerId)
      boundLayers.current.delete(layerId)

      if (mapRef.current) {
        unbindLayerEvents(layerId)
      }
    },
    [mapRef.current]
  )

  const toggleLayerVisibility = useCallback(
    (
      layerKeys: string[],
      visible?: boolean,
      onVisibilityToggle?: (layerId: string, isVisible: boolean) => void
    ): void => {
      const map = mapRef.current
      if (!map) return

      layerKeys.forEach((layerKey) => {
        if (isLayerOnMap(layerKey)) {
          const isVisible = map.getLayoutProperty(layerKey, 'visibility') !== 'none'

          try {
            map.setLayoutProperty(layerKey, 'visibility', (visible ?? isVisible) ? 'none' : 'visible')
          } catch (error) {
            console.warn(`Failed to toggle visibility for layer ${layerKey}:`, error)
          }
        }
      })

      onVisibilityToggle?.(layerKeys[0], visible ?? false) // Callback with first layer and new visibility
    },
    [mapRef.current]
  )

  const showLayerPopover = useCallback((feature: GeoJSONFeature | null): void => {
    if (!feature || !feature.properties) return

    setPopoverInfo((val) => {
      if (val?.popoverType === 'driver') {
        return val
      }
      return feature.properties as PopoverInfo
    })
  }, [])

  const hideLayerPopover = useCallback((): void => {
    setPopoverInfo((val) => {
      if (val?.popoverType === 'driver') {
        return val
      }
      return null
    })
  }, [])

  // Listen for map style changes that might affect layers
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const onStyleData = (): void => {
      // Re-bind events for visible layers after style changes
      layerRegistry.current.forEach((layerInfo: LayerInfo, layerId: string) => {
        if (layerInfo.isVisible && isLayerOnMap(layerId) && !boundLayers.current.has(layerId)) {
          bindLayerEvents(layerId)
        }
      })
    }

    map.on('styledata', onStyleData)

    return () => {
      map.off('styledata', onStyleData)
      // Cleanup all events on unmount
      Array.from(boundLayers.current.keys()).forEach((layerId: string) => {
        unbindLayerEvents(layerId)
      })
    }
  }, [mapRef.current])

  return {
    registerLayer,
    unregisterLayer,
    toggleLayerVisibility,
    showLayerPopover,
    hideLayerPopover,
    popoverInfo,
    setPopoverInfo,
  }
}
